import {
  CollectionDocument,
  DeleteCollectionRequest,
  DeleteCollectionResult,
  parseDeleteCollectionResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import { deleteCollection } from '../../firebase/functions';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';
import { createInventoryMetadataCache } from './inventoryMetadataCache';
import { rapidCaptureDb } from '../rapidCapture/rapidCaptureDb';

export interface CollectionDeletionDependencies {
  createOperationId(): string;
  getCollection(collectionId: string): Promise<CollectionDocument | undefined>;
  purgeLocalAuthority(userId: string, collectionId: string): Promise<number>;
  delete(request: DeleteCollectionRequest): Promise<DeleteCollectionResult>;
}
const defaults = (): CollectionDeletionDependencies => ({
  createOperationId,
  getCollection: (collectionId) =>
    firebaseCollectionRepository.get(collectionId),
  purgeLocalAuthority: async (userId, collectionId) => {
    const [count] = await Promise.all([
      rapidCaptureDb.purgeCollectionAuthority(
        userId,
        collectionId,
        0,
        'collection-deleting',
      ),
      createInventoryMetadataCache(userId).remove(collectionId),
    ]);
    return count;
  },
  delete: async (request) =>
    parseDeleteCollectionResult((await deleteCollection(request)).data),
});

export const createCollectionDeletionController = (
  dependencies: CollectionDeletionDependencies = defaults(),
) => {
  let pending: DeleteCollectionRequest | undefined;
  return {
    async delete(userId: string, collectionId: string, confirmedName: string) {
      if (
        !pending ||
        pending.collectionId !== collectionId ||
        pending.confirmedName !== confirmedName
      ) {
        const collection = await dependencies.getCollection(collectionId);
        if (!collection || collection.lifecycle !== 'active')
          throw new Error('The collection changed. Refresh and try again.');
        if (confirmedName !== collection.name)
          throw new Error(
            'Type the collection name exactly to confirm deletion.',
          );
        pending = {
          collectionId,
          confirmedName,
          operationId: dependencies.createOperationId(),
          expectedCollectionRevision: collection.revision,
          expectedLifecycleVersion: collection.lifecycleVersion,
        };
      }
      const request = pending;
      try {
        const result = await dependencies.delete(request);
        if (
          result.collectionId !== collectionId ||
          result.operationId !== request.operationId
        )
          throw new Error('The deletion result is invalid.');
        // Server acceptance is irreversible; local cleanup failures must not imply deletion failed.
        await dependencies
          .purgeLocalAuthority(userId, collectionId)
          .catch(() => undefined);
        return result;
      } catch (error) {
        if (nestedCode(error) === 'operation-conflict' && pending === request)
          pending = undefined;
        throw error;
      }
    },
    reset() {
      pending = undefined;
    },
  };
};

const nestedCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const value = error as {
    code?: unknown;
    details?: { code?: unknown };
    cause?: unknown;
  };
  if (typeof value.details?.code === 'string') return value.details.code;
  return (
    nestedCode(value.cause, depth + 1) ||
    (typeof value.code === 'string' ? value.code : undefined)
  );
};

export const collectionDeletionErrorMessage = (error: unknown): string => {
  const code = nestedCode(error);
  if (code?.includes('confirmation-required')) {
    return 'Sign in again, then retry this deletion change.';
  }
  if (code?.includes('operation-conflict')) {
    return 'Deletion state changed. Refresh and try again.';
  }
  if (
    code?.includes('role-required') ||
    code?.includes('membership-required')
  ) {
    return 'You are no longer authorized to delete this collection.';
  }
  if (code?.includes('collection-unavailable')) {
    return 'Collection deletion is currently unavailable.';
  }
  if (
    error instanceof Error &&
    error.message.startsWith('The collection changed.')
  ) {
    return error.message;
  }
  return 'Unable to delete the collection. Check your connection and try again.';
};
